/**
 * Custom cart drawer — replaces the third-party Slide Cart app.
 *
 * Uses Shopify's Section Rendering API: every cart mutation POSTs to the
 * standard /cart/*.js endpoints with a `sections` param, and the returned
 * HTML for the `cart-drawer` section is swapped straight in. Discount
 * de-duplication logic stays server-side Liquid (see sections/cart-drawer.liquid)
 * — this file only handles wiring, not business logic.
 *
 * Also shims window.SLIDECART_OPEN / SLIDECART_CLOSE / SLIDECART_UPDATE and
 * fires the `slidecart:updated` window event, so assets/gift-with-purchase.js
 * (a separate, unrelated feature) keeps working unmodified.
 */
(function () {
  var SECTION_ID = 'cart-drawer';
  // The standalone /cart page (snippets/site-cart.liquid, rendered by
  // sections/cart-template.liquid) used to rebuild itself client-side with a
  // Handlebars template on every quantity change — except Handlebars.js was
  // never actually loaded (layout/theme.liquid has it commented out), so
  // that always threw and left #Cart empty. Folding its quantity/remove
  // controls into this same request/render cycle fixes that outright and
  // means it gets the same self-correcting sample cap, race-safe queue, etc.
  // for free instead of a second, parallel implementation.
  var CART_PAGE_SECTION_ID = 'cart-template';

  function drawerRoot() {
    return document.getElementById('CustomCartDrawer');
  }

  function sectionWrapper() {
    return document.getElementById('shopify-section-' + SECTION_ID);
  }

  function cartPageSectionWrapper() {
    return document.getElementById('shopify-section-' + CART_PAGE_SECTION_ID);
  }

  // Only ask the server to also render cart-template (which pulls in several
  // product recommendation blocks — not cheap) when we're actually on the
  // /cart page, rather than on every cart mutation sitewide.
  function requestedSections() {
    var ids = [SECTION_ID];
    if (document.getElementById('Cart')) ids.push(CART_PAGE_SECTION_ID);
    return ids;
  }

  function open() {
    var root = drawerRoot();
    if (!root) return;
    root.classList.add('is-open');
    document.documentElement.classList.add('ccd-open');
    // The legacy jQuery cart toggle (assets/shop.js.liquid, still bound to
    // the same .js-cartToggle-js header icon) also flips this class; force
    // it here so scroll-lock (see snippets/theme-style.liquid) stays correct
    // no matter which handler fired last or whether the drawer was opened
    // programmatically (e.g. after an add-to-cart) without a real icon click.
    document.body.classList.add('cart--is-visible');

    // Products can also reach the cart through paths this file never sees
    // (a plain product-page add-to-cart form, a quick-add elsewhere, the
    // legacy jQuery AJAX cart in shop.js.liquid calling the SLIDECART_OPEN
    // shim directly) — none of those trigger render(), so the sample-cap
    // self-correction in processSampleOverage() never ran for them. Refresh
    // on every open so the drawer always reflects (and, if needed, corrects)
    // the true current cart before the customer sees it.
    refresh();
  }

  function close() {
    var root = drawerRoot();
    if (!root) return;
    root.classList.remove('is-open');
    document.documentElement.classList.remove('ccd-open', 'ccd-typing');
    document.body.classList.remove('cart--is-visible');
    document.body.style.width = '';
  }

  function syncBadge(cart) {
    var badge = document.getElementById('CartToggleItemCount');
    if (badge) {
      badge.textContent = cart.item_count;
      badge.setAttribute('data-count', cart.item_count);
    }
  }

  function render(sections) {
    var html = sections && sections[SECTION_ID];
    if (!html) return;

    // Swapping the section's innerHTML replaces #CustomCartDrawer itself with
    // a fresh server-rendered copy, which never has `is-open` on it (that
    // class only ever gets added client-side). Without restoring it, the
    // drawer would visually close on every cart mutation (quantity change,
    // discount apply, sample add, ...) while it's open.
    var wasOpen = !!(drawerRoot() && drawerRoot().classList.contains('is-open'));

    // A re-render replaces the gift name field; if the customer is typing in
    // it (mobile keyboard open), keep their text and focus so a background
    // refresh doesn't close the keyboard under their finger.
    var activeEl = document.activeElement;
    var keepName = activeEl && activeEl.classList && activeEl.classList.contains('ccd__gift-name-input')
      ? { value: activeEl.value, start: activeEl.selectionStart, end: activeEl.selectionEnd }
      : null;

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newSection = doc.getElementById('shopify-section-' + SECTION_ID);
    var current = sectionWrapper();
    if (newSection && current) {
      current.innerHTML = newSection.innerHTML;
    }

    if (keepName) {
      var newName = document.querySelector('.ccd__gift-name-input');
      if (newName) {
        newName.value = keepName.value;
        newName.focus({ preventScroll: true });
        try { newName.setSelectionRange(keepName.start, keepName.end); } catch (e) {}
      }
    }

    if (wasOpen) {
      var root = drawerRoot();
      if (root) root.classList.add('is-open');
    }

    var cartPageHtml = sections && sections[CART_PAGE_SECTION_ID];
    if (cartPageHtml) {
      var cartDoc = new DOMParser().parseFromString(cartPageHtml, 'text/html');
      var newCartSection = cartDoc.getElementById('shopify-section-' + CART_PAGE_SECTION_ID);
      var currentCartSection = cartPageSectionWrapper();
      if (newCartSection && currentCartSection) {
        currentCartSection.innerHTML = newCartSection.innerHTML;
      }
    }

    processAutoGift();
    processSampleOverage();
  }

  // sections/cart-drawer.liquid checks whether settings.auto_gift_trigger_product
  // is in the cart and whether the tagged gift line (properties._auto_gift)
  // matches that, exposing data-auto-gift-action="add:<variantId>" or
  // "remove:<key>". Acting on it here re-renders the section, which
  // re-evaluates the same check against the now-current cart — so it
  // self-corrects (add when the trigger appears, remove again once it's
  // gone) without any client-side product/collection knowledge.
  //
  // render() can fire from several concurrent sources now (the add itself,
  // open()'s own refresh(), a stray refresh() from assets/shop.js.liquid...).
  // Two of those can both read the same "gift not present yet" snapshot and
  // both fire an add before either has landed, giving the customer 2 gifts
  // instead of 1. autoGiftPending guards against acting twice at once; the
  // next real render (once the in-flight request's response comes back)
  // re-checks the actual state, so nothing is missed, just de-duplicated.
  var autoGiftPending = false;
  var autoGiftChain = 0;

  function processAutoGift() {
    if (autoGiftPending) return;

    var root = drawerRoot();
    var action = root && root.getAttribute('data-auto-gift-action');
    var isExtraShipping = false;
    if (!action) {
      // Second parcel for "ship my products now": same add/remove pattern,
      // handled after the gift itself is settled.
      action = root && root.getAttribute('data-extra-shipping-action');
      isExtraShipping = !!action;
    }
    if (!action) {
      autoGiftChain = 0;
      return;
    }

    var request;
    if (action.indexOf('add:') === 0) {
      var giftProps;
      if (isExtraShipping) {
        giftProps = { _extra_shipping: 'true' };
      } else {
        giftProps = { _auto_gift: 'true', _delivery: 'together' };
        // New gift lines start on "ship everything together" (see the delivery
        // options on the gift line in sections/cart-drawer.liquid).
        var giftDelivery = root.getAttribute('data-auto-gift-delivery');
        if (giftDelivery) giftProps['Levering'] = giftDelivery;
      }
      request = addToCart(action.slice(4), 1, giftProps);
    } else if (action.indexOf('remove:') === 0) {
      request = changeLine(action.slice(7), 0);
    } else if (action.indexOf('limit:') === 0) {
      // Only one gift per order: bring a higher quantity back to 1.
      request = changeLine(action.slice(6), 1);
    }
    if (!request) return;

    autoGiftPending = true;
    request.then(function (result) {
      autoGiftPending = false;
      // The render triggered by this request ran while the guard was still
      // set, so it skipped its own check. With several gifts to add or remove
      // one at a time, look again now (only after a success, so a failing
      // add can't loop; the chain is capped as a second safety).
      if (result && result.ok && autoGiftChain < 20) {
        autoGiftChain += 1;
        processAutoGift();
      } else {
        autoGiftChain = 0;
      }
    });
  }

  // sections/cart-drawer.liquid caps the combined quantity of
  // settings.free_samples_collection lines at settings.free_samples_cap. The
  // "+" stepper is disabled server-side once that cap is reached, but the qty
  // input can still be typed past it directly — data-sample-overage-action
  // ("<line key>:<allowed qty>") flags that line so it gets pulled back down
  // on the next render, same self-correcting pattern as processAutoGift
  // (including the same concurrent-render guard, see sampleOveragePending).
  var sampleOveragePending = false;

  function processSampleOverage() {
    if (sampleOveragePending) return;

    var root = drawerRoot();
    var action = root && root.getAttribute('data-sample-overage-action');
    if (!action) return;

    var separator = action.indexOf(':');
    if (separator === -1) return;

    var key = action.slice(0, separator);
    var qty = parseInt(action.slice(separator + 1), 10);
    if (!key || Number.isNaN(qty)) return;

    sampleOveragePending = true;
    changeLine(key, qty).then(function () {
      sampleOveragePending = false;
      // render() (triggered by this very changeLine call) already re-ran
      // processSampleOverage() once, but that nested call landed while
      // sampleOveragePending was still true and was dropped — so a cart
      // with several lines over the cap (e.g. added directly from a sample
      // product's own page, bypassing the picker's cap-aware UI) only had
      // its first offending line corrected. Re-check now that the flag is
      // clear: if data-sample-overage-action still points at another line,
      // this keeps going; once none remains, the empty key short-circuits.
      processSampleOverage();
    });
  }

  // Several places now trigger a cart fetch independently of each other (the
  // drawer's own qty controls, refresh() on drawer open, and — since these
  // pages/flows don't otherwise touch this file — the /cart page and the
  // sticky add-to-cart bar in assets/shop.js.liquid). Two of those requests
  // can be in flight at once and resolve out of order, e.g. a plain refresh()
  // started just before a customer's own removal can still land *after* the
  // removal's response and repaint the drawer with the pre-removal quantities
  // — the line would visibly "come back". Every request that ends in
  // applyCartResponse is tagged with a sequence number, and a response is
  // only ever applied if no later request has already been applied.
  var requestSeq = 0;
  var appliedSeq = 0;

  function nextRequestId() {
    return ++requestSeq;
  }

  function applyCartResponse(data, requestId) {
    if (requestId !== undefined) {
      if (requestId < appliedSeq) return;
      appliedSeq = requestId;
    }

    render(data.sections);

    // /cart/add.js only returns the added line item(s), not the full cart,
    // so it has no item_count. /cart/change.js and /cart/update.js do return
    // the full cart. When it's missing, fetch it so the badge still updates
    // immediately instead of waiting for the next cart mutation.
    if (typeof data.item_count === 'number') {
      syncBadge(data);
    } else {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (response) { return response.json(); })
        .then(syncBadge);
    }

    window.dispatchEvent(new CustomEvent('slidecart:updated'));
    window.dispatchEvent(new CustomEvent('cart:changed'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));

    // A customer removing the auto-gift line by hand sets the
    // _auto_gift_removed cart attribute (see removeAutoGift) so it isn't
    // immediately forced back in. That attribute otherwise lives on the cart
    // forever, including once the cart is emptied out entirely — so a later,
    // unrelated shopping session on the same browser would never get the
    // gift again either. Clear it once the cart is genuinely empty, so a
    // fresh trigger product gets a fresh shot at the gift.
    if (data.item_count === 0 && data.attributes && data.attributes._auto_gift_removed) {
      enqueue(function () {
        return postJSON('/cart/update.js', { attributes: { _auto_gift_removed: '' } });
      });
    }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        return { ok: response.ok, data: data };
      });
    });
  }

  // Every cart-mutating call below used to fire its own fetch independently.
  // With several places now able to trigger one at once (the drawer's own
  // controls, open()'s refresh(), assets/shop.js.liquid's refresh() calls,
  // the self-correcting add-gift/reduce-sample logic reacting to a render),
  // two requests could be in flight together — and since the server can
  // process/answer them in either order, a request sent later could still
  // resolve first and get treated as "the latest" while actually reflecting
  // an older cart snapshot (e.g. before a gift add had landed), overwriting
  // the correct state until the next full page load. requestSeq (below)
  // guards against a stale response landing *after* a newer one, but only
  // running one request at a time in the first place removes the race
  // entirely: each task only starts once the previous one's full handling
  // (fetch + applyCartResponse) has finished, so "latest sent" and "latest
  // applied" can never disagree.
  var requestQueue = Promise.resolve();

  function enqueue(task) {
    var result = requestQueue.then(task, task);
    // Keep the queue usable even if a request fails, and don't let one
    // rejection sour every request queued behind it.
    requestQueue = result.catch(function () {});
    return result;
  }

  function addToCart(id, quantity, properties) {
    return enqueue(function () {
      var requestId = nextRequestId();
      var item = { id: id, quantity: quantity || 1 };
      if (properties) item.properties = properties;
      return postJSON('/cart/add.js', {
        items: [item],
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  function changeLine(key, quantity) {
    return enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/change.js', {
        id: key,
        quantity: quantity,
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  // A customer removing the automatic gift line also sets the
  // _auto_gift_removed cart attribute in the same request, so
  // sections/cart-drawer.liquid doesn't immediately re-add it while the
  // trigger is still in the cart. Keyed by line key so a separately bought
  // line of the same product is untouched. processAutoGift's own removal
  // keeps using changeLine, so it never sets the flag.
  function removeAutoGift(key) {
    return enqueue(function () {
      var requestId = nextRequestId();
      var updates = {};
      updates[key] = 0;
      return postJSON('/cart/update.js', {
        updates: updates,
        attributes: { _auto_gift_removed: 'true' },
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  // Line changes made by the customer (remove button, qty buttons, qty input).
  // .ccd__item is a drawer line, .line-item its /cart page equivalent
  // (snippets/site-cart.liquid) — both carry the same data-auto-gift marker.
  function customerChangeLine(el, key, quantity) {
    var line = el.closest('.ccd__item, .line-item');
    if (quantity === 0 && line && line.hasAttribute('data-auto-gift')) {
      return removeAutoGift(key);
    }
    // A drawer line that shows several cart lines as one (data-merge-keys, see
    // sections/cart-drawer.liquid): the shown line takes the new total and the
    // hidden ones go to 0, so the cart ends up with a single line.
    var extraKeys = line && line.getAttribute('data-merge-keys');
    if (extraKeys) {
      var updates = {};
      updates[key] = quantity;
      extraKeys.split(',').forEach(function (extraKey) {
        if (extraKey) updates[extraKey] = 0;
      });
      return changeLines(updates);
    }
    return changeLine(key, quantity);
  }

  function changeLines(updates) {
    return enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/update.js', {
        updates: updates,
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  function refresh() {
    return enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/update.js', { sections: requestedSections() }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  function applyDiscounts(codes) {
    return enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/update.js', {
        discount: codes.join(','),
        sections: requestedSections(),
      }).then(function (result) {
        applyCartResponse(result.data, requestId);
        return result;
      });
    });
  }

  function existingDiscountCodes() {
    var pills = document.querySelectorAll('.ccd__discount-pill[data-code]');
    return Array.prototype.map.call(pills, function (el) {
      return el.getAttribute('data-code');
    });
  }

  // Add-to-cart form intercept (sitewide, e.g. #prod_add_to_cart on product pages,
  // .add-to-bag on the product page's cross-sell bundle) — keeps the shopper on the
  // page and opens the cart drawer instead of a full-page /cart/add navigation.
  document.addEventListener(
    'click',
    function (event) {
      var trigger = event.target.closest('#prod_add_to_cart, .add-to-bag');
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var form = trigger.closest('form[action="/cart/add"]');
      if (!form) return;

      var formData = new FormData(form);
      formData.append('sections', requestedSections().join(','));

      enqueue(function () {
        var requestId = nextRequestId();
        return fetch('/cart/add.js', { method: 'POST', body: formData })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data };
            });
          })
          .then(function (result) {
            if (!result.ok) {
              console.error('Error adding to cart:', result.data);
              return;
            }
            applyCartResponse(result.data, requestId);
            open();
          })
          .catch(function (error) {
            console.error('Error adding to cart:', error);
          });
      });
    },
    true
  );

  // Drawer open/close + in-drawer interactions
  // "Add again" for a gift the customer removed by hand: clearing the
  // _auto_gift_removed cart attribute lets the normal gift rules add it again
  // (the re-render below runs processAutoGift against the cleared state).
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-ccd-gift-readd]');
    if (!btn) return;
    btn.disabled = true;
    enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/update.js', {
        attributes: { _auto_gift_removed: '' },
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        else btn.disabled = false;
        return result;
      });
    });
  });

  document.addEventListener('click', function (event) {
    if (event.target.closest('.js-cartToggle-js')) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-ccd-close]')) {
      close();
      return;
    }

    var qtyBtn = event.target.closest('.ccd__qty-btn');
    if (qtyBtn) {
      var wrapper = qtyBtn.closest('.ccd__qty');
      var input = wrapper.querySelector('.ccd__qty-input');
      var key = wrapper.getAttribute('data-key');
      var qty = parseInt(input.value || '0', 10);
      if (Number.isNaN(qty)) qty = 0;
      qty = qtyBtn.getAttribute('data-action') === 'increase' ? qty + 1 : qty - 1;
      if (qty < 0) qty = 0;
      qtyBtn.disabled = true;
      customerChangeLine(qtyBtn, key, qty);
      return;
    }

    var removeBtn = event.target.closest('.ccd__item-remove');
    if (removeBtn) {
      customerChangeLine(removeBtn, removeBtn.getAttribute('data-key'), 0);
      return;
    }

    // /cart page (snippets/site-cart.liquid) quantity stepper and remove
    // icon — the .line-item equivalents of the drawer's .ccd__qty-btn /
    // .ccd__item-remove above. See customerChangeLine for the shared
    // auto-gift handling and render() for how #Cart gets its fresh HTML.
    var cartQtyStep = event.target.closest('#Cart .inputCounter-up, #Cart .inputCounter-down');
    if (cartQtyStep) {
      var cartLine = cartQtyStep.closest('.line-item');
      if (!cartLine) return;
      if (cartQtyStep.classList.contains('inputCounter-up') && cartLine.hasAttribute('data-sample-cap-reached')) {
        return;
      }
      var cartInput = cartLine.querySelector('.inputCounter');
      var cartQty = parseInt((cartInput && cartInput.value) || '0', 10);
      if (Number.isNaN(cartQty)) cartQty = 0;
      cartQty = cartQtyStep.classList.contains('inputCounter-up') ? cartQty + 1 : cartQty - 1;
      if (cartQty < 0) cartQty = 0;
      customerChangeLine(cartQtyStep, cartLine.getAttribute('data-key'), cartQty);
      return;
    }

    var cartRemoveBtn = event.target.closest('#Cart .cart-product-remove');
    if (cartRemoveBtn) {
      var cartRemoveLine = cartRemoveBtn.closest('.line-item');
      if (cartRemoveLine) {
        customerChangeLine(cartRemoveBtn, cartRemoveLine.getAttribute('data-key'), 0);
      }
      return;
    }

    var samplesNavBtn = event.target.closest('.ccd__samples-nav');
    if (samplesNavBtn) {
      var carousel = samplesNavBtn.closest('.ccd__samples-carousel');
      var row = carousel && carousel.querySelector('.ccd__samples-row');
      if (row) {
        var direction = samplesNavBtn.getAttribute('data-ccd-samples-scroll') === 'prev' ? -1 : 1;
        var step = row.clientWidth * 0.9;
        if (row.scrollBy) {
          row.scrollBy({ left: direction * step, behavior: 'smooth' });
        } else {
          row.scrollLeft += direction * step;
        }
      }
      return;
    }

    var sampleAddBtn = event.target.closest('.ccd__sample-add');
    if (sampleAddBtn && !sampleAddBtn.disabled) {
      var originalLabel = sampleAddBtn.textContent;
      sampleAddBtn.disabled = true;
      sampleAddBtn.textContent = '...';
      addToCart(sampleAddBtn.getAttribute('data-variant-id'), 1).then(function (result) {
        if (!result.ok) {
          sampleAddBtn.disabled = false;
          sampleAddBtn.textContent = originalLabel;
        }
      });
      return;
    }

    var removeDiscountBtn = event.target.closest('[data-ccd-remove-discount]');
    if (removeDiscountBtn) {
      var code = removeDiscountBtn.getAttribute('data-ccd-remove-discount');
      var remaining = existingDiscountCodes().filter(function (c) {
        return c !== code;
      });
      applyDiscounts(remaining);
    }
  });

  // Click-and-drag ("slepen") to scroll the sample tile row with a mouse,
  // since overflow-x: auto alone only supports scrollbar-drag/touch, not a
  // plain mouse drag on the content itself.
  var sampleDrag = null;
  var suppressSampleClick = false;

  document.addEventListener('mousedown', function (event) {
    var row = event.target.closest('.ccd__samples-row');
    if (!row) return;
    sampleDrag = { row: row, startX: event.pageX, startScrollLeft: row.scrollLeft, moved: false };
    row.classList.add('is-dragging');
  });

  document.addEventListener('mousemove', function (event) {
    if (!sampleDrag) return;
    var delta = event.pageX - sampleDrag.startX;
    if (Math.abs(delta) > 3) sampleDrag.moved = true;
    sampleDrag.row.scrollLeft = sampleDrag.startScrollLeft - delta;
    event.preventDefault();
  });

  function endSampleDrag() {
    if (!sampleDrag) return;
    sampleDrag.row.classList.remove('is-dragging');
    if (sampleDrag.moved) suppressSampleClick = true;
    sampleDrag = null;
  }

  document.addEventListener('mouseup', endSampleDrag);
  document.addEventListener('mouseleave', endSampleDrag);

  // A drag ending on a link/button inside the row (e.g. a product image)
  // would otherwise also fire a click right after and navigate away/add a
  // sample; swallow that one click.
  document.addEventListener(
    'click',
    function (event) {
      if (suppressSampleClick) {
        suppressSampleClick = false;
        if (event.target.closest('.ccd__samples-row')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },
    true
  );

  // The sample tile row only scrolls horizontally (overflow-x: auto); a plain
  // mouse wheel scrolls vertically and does nothing there by default, so
  // translate vertical wheel delta into horizontal scrolling ourselves.
  document.addEventListener(
    'wheel',
    function (event) {
      var row = event.target.closest('.ccd__samples-row');
      if (!row) return;
      if (row.scrollWidth <= row.clientWidth) return;

      var delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      row.scrollLeft += delta;
      event.preventDefault();
    },
    { passive: false }
  );

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-ccd-discount-form]');
    if (!form) return;
    event.preventDefault();

    var input = form.querySelector('input[name="discount"]');
    var value = (input.value || '').trim();
    if (!value) return;

    var codes = existingDiscountCodes();
    if (codes.indexOf(value) !== -1) return;

    applyDiscounts(codes.concat([value])).then(function (result) {
      var applied =
        result.data.discount_codes &&
        result.data.discount_codes.filter(function (d) {
          return d.code === value;
        })[0];
      var errorEl = document.querySelector('[data-ccd-discount-error]');
      if (applied && applied.applicable === false) {
        if (errorEl) errorEl.classList.remove('hidden');
      } else if (errorEl) {
        errorEl.classList.add('hidden');
      }
    });

    input.value = '';
  });

  // Typing a quantity (unlike the +/- buttons, which the server already
  // disables once the sample cap is reached — see sections/cart-drawer.liquid)
  // can otherwise ask for any number directly. Clamp it here, client-side,
  // before the request is even sent, so an over-cap quantity is never briefly
  // live in the cart (Shopify's free-samples discount would otherwise apply
  // to it for the moment before the server-side self-correction caught up).
  function clampSampleQty(input, lineSelector, sampleInputsSelector) {
    var line = input.closest(lineSelector);
    if (!line || !line.hasAttribute('data-is-sample')) return null;

    var root = drawerRoot();
    var cap = parseInt(root && root.getAttribute('data-sample-cap'), 10);
    if (Number.isNaN(cap)) return null;

    var othersTotal = 0;
    var sampleInputs = document.querySelectorAll(sampleInputsSelector);
    Array.prototype.forEach.call(sampleInputs, function (el) {
      if (el === input) return;
      var otherQty = parseInt(el.value || '0', 10);
      if (!Number.isNaN(otherQty)) othersTotal += otherQty;
    });

    var allowed = cap - othersTotal;
    if (allowed < 0) allowed = 0;
    return allowed;
  }

  // Name for the automatic gift bag: written to the gift line's own
  // "Naam op toilettas" property. /cart/change.js replaces the whole
  // properties object, so _auto_gift has to be sent along again or the line
  // would stop counting as the gift (and could be added a second time).
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-gift-name-form]');
    if (!form) return;
    event.preventDefault();

    var key = form.getAttribute('data-key');
    var name = form.querySelector('.ccd__gift-name-input').value.trim().slice(0, 16);
    var li = form.closest('.ccd__item');
    var qtyInput = li && li.querySelector('.ccd__qty-input');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    var properties = { _auto_gift: 'true', 'Naam op toilettas': name };
    var delivery = form.querySelector('input[name="gift_delivery"]:checked');
    if (delivery) {
      properties['Levering'] = delivery.value;
      properties._delivery = delivery.getAttribute('data-code') || 'together';
    }

    enqueue(function () {
      var requestId = nextRequestId();
      return postJSON('/cart/change.js', {
        id: key,
        quantity: qty,
        properties: properties,
        sections: requestedSections(),
      }).then(function (result) {
        if (result.ok) applyCartResponse(result.data, requestId);
        return result;
      });
    });
  });

  // Picking a delivery option saves straight away, so it can't be forgotten.
  document.addEventListener('change', function (event) {
    var radio = event.target.closest('input[name="gift_delivery"]');
    var form = radio && radio.closest('[data-gift-name-form]');
    if (form) form.requestSubmit();
  });

  // Keep the name field in view once the mobile keyboard has opened.
  document.addEventListener('focusin', function (event) {
    var input = event.target.closest('.ccd__gift-name-input');
    if (!input) return;
    clearTimeout(typingTimer);
    document.documentElement.classList.add('ccd-typing');
    setTimeout(function () {
      input.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 300);
  });

  // Short delay so tapping "Opslaan" still lands on the button before the
  // footer comes back and shifts the layout.
  var typingTimer;
  document.addEventListener('focusout', function (event) {
    if (!event.target.closest('.ccd__gift-name-input')) return;
    clearTimeout(typingTimer);
    typingTimer = setTimeout(function () {
      document.documentElement.classList.remove('ccd-typing');
    }, 200);
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest('.ccd__qty-input');
    if (!input) return;

    clearTimeout(input._ccdTimer);
    input._ccdTimer = setTimeout(function () {
      var key = input.getAttribute('data-key');
      var qty = parseInt(input.value || '0', 10);
      if (Number.isNaN(qty) || qty < 0) qty = 0;
      var allowed = clampSampleQty(input, '.ccd__item', '.ccd__item[data-is-sample] .ccd__qty-input');
      if (allowed !== null && qty > allowed) qty = allowed;
      input.value = qty;
      customerChangeLine(input, key, qty);
    }, 500);
  });

  // /cart page equivalent of the drawer's .ccd__qty-input handler above —
  // 'focusout' bubbles (unlike 'blur'), so it works with plain document-level
  // delegation the same way the rest of this file's listeners do.
  document.addEventListener('focusout', function (event) {
    var input = event.target.closest('#Cart .inputCounter');
    if (!input) return;

    var line = input.closest('.line-item');
    if (!line) return;

    var key = line.getAttribute('data-key');
    var qty = parseInt(input.value || '0', 10);
    if (Number.isNaN(qty) || qty < 0) qty = 0;
    var allowed = clampSampleQty(input, '.line-item', '.line-item[data-is-sample] .inputCounter');
    if (allowed !== null && qty > allowed) qty = allowed;
    input.value = qty;
    customerChangeLine(input, key, qty);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') close();
  });

  // Shim the Slide Cart app's public API so unrelated glue code (e.g.
  // gift-with-purchase.js) that still calls it keeps working unmodified.
  window.SLIDECART_OPEN = open;
  window.SLIDECART_CLOSE = close;
  window.SLIDECART_UPDATE = refresh;

  window.CartDrawer = {
    open: open,
    close: close,
    refresh: refresh,
    addToCart: addToCart,
    changeLine: changeLine,
    applyCartResponse: applyCartResponse,
  };

  // The initial page load already has a server-rendered #CustomCartDrawer
  // with a correct data-auto-gift-action attribute (no fetch involved), but
  // render() — where it's normally acted on — only runs after a cart
  // mutation. Run it once up front too, so a trigger product already sitting
  // in the cart from an earlier visit still gets its gift added without
  // requiring another cart change first.
  processAutoGift();
  processSampleOverage();

  // The checks above only run on a fresh page load or after a cart mutation
  // made through this file. A sample can still slip past them when the page
  // is restored from the back/forward cache, when the cart was changed in
  // another tab, or when the customer goes straight to checkout before the
  // drawer ever re-rendered. Re-sync in those cases so a sample past
  // settings.free_samples_cap is removed immediately.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') refresh();
  });

  // Re-sync (and let processSampleOverage() trim) before letting a checkout
  // click through. A trimmed line can leave the next line over the cap, so
  // repeat a few times until the drawer reports no overage left.
  function settleSampleCap(tries) {
    return refresh().then(function () {
      return refresh();
    }).then(function () {
      var root = drawerRoot();
      var action = (root && root.getAttribute('data-sample-overage-action')) || '';
      if (action.split(':')[0] && tries > 0) return settleSampleCap(tries - 1);
    });
  }

  var checkoutGuardPassed = false;
  document.addEventListener(
    'click',
    function (event) {
      if (checkoutGuardPassed) return;
      var trigger = event.target.closest(
        'a[href="/checkout"], a[href^="/checkout?"], button[name="checkout"], input[name="checkout"], .ccd__checkout-btn'
      );
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      settleSampleCap(5).catch(function () {}).then(function () {
        checkoutGuardPassed = true;
        trigger.click();
        checkoutGuardPassed = false;
      });
    },
    true
  );
})();
